import moment from "moment";

// Panel Auto-Refresh — the arm decision, kept pure and side-effect free so it can be
// unit tested without mounting React. AutoRefreshManager owns the timers, the
// sessionStorage markers and the reload; this file only answers one question:
// "given the latest server payload, should the 30 second banner be showing right now?"
//
// Priority order is deliberate — the most recently requested refresh wins:
//   MANUAL    an admin fired POST /trigger-panel-refresh and this tab has not seen it.
//             `active` is intentionally NOT required: a tab that was asleep across the
//             grace window should still refresh once when it wakes. Callers dedupe on
//             the id (sessionStorage), so it stays exactly once per tab.
//   SCHEDULED we are inside the last N seconds before the next boundary. The deadline
//             is the real boundary, not a fresh 30s, so the countdown is honest.
//   MISSED    the window in effect changed since this tab loaded, i.e. it was open or
//             asleep across a boundary and is now showing stale data.

export const REFRESH_REASON = {
  SCHEDULED: "scheduled",
  MISSED: "missed",
  MANUAL: "manual"
};

export function decidePanelRefresh({ data, nowMs, seenWindow, seenManual, reloadLocked }) {
  if (!data || !data.enabled || reloadLocked) return null;

  const warningSeconds = data.warning_seconds > 0 ? data.warning_seconds : 30;
  const warningMs = warningSeconds * 1000;
  const manual = data.manual_refresh;

  if (manual && manual.id && String(manual.id) !== String(seenManual)) {
    return {
      armedFor: REFRESH_REASON.MANUAL,
      deadline: nowMs + warningMs,
      windowId: data.current_window_id || null,
      manualId: manual.id
    };
  }

  if (
    data.next_refresh_at &&
    data.seconds_until_next > 0 &&
    data.seconds_until_next <= warningSeconds &&
    data.next_refresh_at !== seenWindow
  ) {
    return {
      armedFor: REFRESH_REASON.SCHEDULED,
      deadline: moment(data.next_refresh_at).valueOf(),
      windowId: data.next_refresh_at,
      manualId: manual && manual.id ? manual.id : null
    };
  }

  if (data.current_window_id) {
    // Temporal, not string inequality. The marker can legitimately point at a
    // boundary in the FUTURE: when a scheduled refresh fires 20s early we mark the
    // boundary we refreshed into, so the tab does not immediately re-arm at the real
    // boundary. A `!==` would see "12:00" !== "16:00" and report a missed window.
    // A window is only "missed" if it moved past the one we last handled.
    const movedPast = !seenWindow || moment(data.current_window_id).isAfter(moment(seenWindow));
    if (movedPast) {
      return {
        armedFor: REFRESH_REASON.MISSED,
        deadline: nowMs + warningMs,
        windowId: data.current_window_id,
        manualId: null
      };
    }
  }

  return null;
}
